/**
 * Pending saves for the staff testing flow on weak or dropping wifi.
 *
 * Every testing save (FMS toggles, ROM degrees, posture landmarks, ROM analysis, ROM clips) goes
 * through `queueSave`. The latest value for each item is kept:
 *   - in memory, and (for small JSON values) in localStorage, until the server confirms it;
 *   - retried automatically when the browser comes back online, on tab focus, and on an interval.
 *
 * One save per item runs at a time and only the newest value is sent, so rapid edits on a slow
 * connection never race each other. Runners for each kind are registered in
 * `pendingSaveRunners.ts`, so items left over from before a page reload can still be sent even if
 * the screen that made them is not open.
 *
 * Each item belongs to the staff member who made it: it is only sent, counted or restored while
 * that same person is signed in, so another login on a shared iPad never replays or sees it.
 *
 * A save the server refuses outright (permission, a record that no longer exists) is not retried
 * forever: after a few refusals it is marked "failed", taken off the device and shown to staff.
 *
 * This is phase 1 hardening, not full offline mode: the app still needs a connection to load.
 */
import { useSyncExternalStore } from "react";

export type PendingSaveKind =
  | "fms_manual"
  | "rom_degrees"
  | "rom_analysis"
  | "rom_video"
  | "posture_side"
  | "posture_front";

export type PendingSaveStatus = "saving" | "waiting" | "failed";

type StoredEntry = {
  v: 1;
  key: string;
  kind: PendingSaveKind;
  /** Auth user who made this save; only sent while they are signed in. */
  userId: string | null;
  orgId: string;
  employeeId: string;
  payload: unknown;
  queuedAt: number;
};

type Runner = (payload: unknown) => Promise<unknown>;

type LiveEntry = {
  stored: StoredEntry;
  persist: boolean;
  /** Not in localStorage, but kept on the device elsewhere (ROM clips, in IndexedDB). */
  durable: boolean;
  status: PendingSaveStatus;
  inFlight: boolean;
  /** A newer value arrived while a save was running; send it as soon as that save ends. */
  again: boolean;
  /** Discarded while a save was running: drop it when that save ends, whatever the outcome. */
  discarded: boolean;
  /** Loaded from storage after a page reload (see `confirmedAfterReload`). */
  fromReload: boolean;
  /** Consecutive refusals from the server (not connection failures). */
  refusals: number;
  lastError: string | null;
  run?: Runner;
  onSaved?: (result: unknown, payload: unknown) => void;
};

const STORAGE_PREFIX = "f2l.pendingSave.v1.";
const RETRY_INTERVAL_MS = 15_000;
/** Refusals in a row before an item is given up on. */
const MAX_REFUSALS = 3;
/** Items older than this left on a device are dropped on load (nothing left to retry for). */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Server answers that retrying cannot fix: foreign key (the record is gone), not-null / check /
 * bad input, and permission denied. Anything else (no answer, timeouts, 5xx, an expired session)
 * is treated as temporary.
 */
const REFUSAL_CODES = new Set(["23503", "23502", "23514", "22P02", "42501"]);
const REFUSAL_STATUSES = new Set([403, 413]);

const runners: Partial<Record<PendingSaveKind, Runner>> = {};
const entries = new Map<string, LiveEntry>();
/**
 * Values sent from a reload's leftovers, kept for this page session. A screen loading at the same
 * time may have read the server before that save landed; it restores from here instead of
 * showing (and then re-saving over) the older server copy.
 */
const confirmedAfterReload = new Map<string, StoredEntry>();
const listeners = new Set<() => void>();
/** Callers waiting for an item to have no save in flight (see `whenPendingSaveIdle`). */
const idleWaiters = new Map<string, Array<() => void>>();
let retryTimer: ReturnType<typeof setInterval> | null = null;
let started = false;
let currentUserId: string | null = null;

function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function errorText(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  if (e && typeof e === "object" && "message" in e && typeof (e as { message: unknown }).message === "string") {
    return (e as { message: string }).message;
  }
  return "Could not reach the server";
}

function isRefusal(e: unknown): boolean {
  if (!e || typeof e !== "object") return false;
  const { code, status, statusCode } = e as { code?: unknown; status?: unknown; statusCode?: unknown };
  if (typeof code === "string" && REFUSAL_CODES.has(code)) return true;
  const s = Number(status ?? statusCode);
  return Number.isFinite(s) && REFUSAL_STATUSES.has(s);
}

function mine(entry: { stored: StoredEntry } | StoredEntry): boolean {
  const s = "stored" in entry ? entry.stored : entry;
  return (s.userId ?? null) === currentUserId;
}

function writeStorage(entry: StoredEntry): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + entry.key, JSON.stringify(entry));
  } catch {
    /* storage full or disabled: the in-memory copy still retries while the page is open */
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    /* ignore */
  }
}

function readStorage(key: string): StoredEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredEntry;
    return parsed && parsed.v === 1 && parsed.key === key ? parsed : null;
  } catch {
    return null;
  }
}

/* ---------- status snapshot for React ---------- */

export type PendingSaveItemStatus = {
  status: PendingSaveStatus;
  lastError: string | null;
  /** Held in this page's memory only: lost if the page is closed or reloaded before it sends. */
  memoryOnly: boolean;
  /** The caller keeps its own copy on the device (ROM clips), which survives even a refusal. */
  keptOnDevice: boolean;
};

export type PendingSavesSnapshot = {
  online: boolean;
  saving: number;
  waiting: number;
  /** Refused by the server and given up on; shown until staff dismiss them. */
  failed: number;
  /**
   * Not yet saved and held only in memory (video clips), for anyone signed in on this device:
   * a page reload loses these, unlike stored items, which resend after it.
   */
  inMemoryUnsaved: number;
  byKey: Readonly<Record<string, PendingSaveItemStatus>>;
};

let snapshot: PendingSavesSnapshot = {
  online: isOnline(),
  saving: 0,
  waiting: 0,
  failed: 0,
  inMemoryUnsaved: 0,
  byKey: {},
};

function releaseIdleWaiters(): void {
  for (const [key, waiters] of idleWaiters) {
    if (entries.get(key)?.inFlight) continue;
    idleWaiters.delete(key);
    for (const w of waiters) w();
  }
}

function emit(): void {
  releaseIdleWaiters();
  let saving = 0;
  let waiting = 0;
  let failed = 0;
  let inMemoryUnsaved = 0;
  const byKey: Record<string, PendingSaveItemStatus> = {};
  for (const [key, e] of entries) {
    if (!e.persist && !e.durable && e.status !== "failed") inMemoryUnsaved += 1;
    if (!mine(e)) continue;
    if (e.status === "saving") saving += 1;
    else if (e.status === "failed") failed += 1;
    else waiting += 1;
    byKey[key] = {
      status: e.status,
      lastError: e.lastError,
      memoryOnly: !e.persist && !e.durable,
      keptOnDevice: e.durable,
    };
  }
  snapshot = { online: isOnline(), saving, waiting, failed, inMemoryUnsaved, byKey };
  for (const l of listeners) l();
  syncRetryTimer();
}

function subscribe(listener: () => void): () => void {
  ensureStarted();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): PendingSavesSnapshot {
  return snapshot;
}

/** Whole-screen summary for the connection indicator. */
export function usePendingSavesSummary(): PendingSavesSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Status of one item (`null` = nothing outstanding, i.e. saved). */
export function usePendingSaveStatus(key: string | null): PendingSaveItemStatus | null {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return key ? snap.byKey[key] ?? null : null;
}

/* ---------- retry loop ---------- */

function syncRetryTimer(): void {
  const anyWaiting = [...entries.values()].some((e) => e.status === "waiting" && mine(e));
  if (anyWaiting && !retryTimer) {
    retryTimer = setInterval(retryAllPendingSaves, RETRY_INTERVAL_MS);
  } else if (!anyWaiting && retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
}

/** Try every waiting item again (no-op while the browser reports it is offline). */
export function retryAllPendingSaves(): void {
  if (!isOnline()) {
    emit();
    return;
  }
  for (const key of entries.keys()) {
    const e = entries.get(key);
    if (e && !e.inFlight && e.status === "waiting" && mine(e)) void attempt(key);
  }
}

/**
 * Tell the queue who is signed in (null when signed out). Items only run for their own author;
 * a sign-out leaves them on the device for that person's next sign-in.
 */
/** Staff member the queue is currently sending for (null when signed out). */
export function currentPendingSaveUser(): string | null {
  return currentUserId;
}

export function setPendingSaveUser(userId: string | null): void {
  if (currentUserId === userId) return;
  currentUserId = userId;
  emit();
  retryAllPendingSaves();
}

/** Load items saved locally before a reload, and listen for the connection coming back. */
function ensureStarted(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  const now = Date.now();
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k.slice(STORAGE_PREFIX.length));
    }
    for (const key of keys) {
      const stored = readStorage(key);
      if (!stored || now - stored.queuedAt > MAX_AGE_MS) {
        if (stored) console.warn(`[Fit2Lift save] dropping ${key}: unsent for over 30 days`);
        removeStorage(key);
        continue;
      }
      if (!entries.has(stored.key)) {
        entries.set(stored.key, {
          stored,
          persist: true,
          durable: false,
          status: "waiting",
          inFlight: false,
          again: false,
          discarded: false,
          fromReload: true,
          refusals: 0,
          lastError: null,
        });
      }
    }
  } catch {
    /* storage unavailable */
  }
  window.addEventListener("online", () => retryAllPendingSaves());
  window.addEventListener("offline", () => emit());
  window.addEventListener("focus", () => retryAllPendingSaves());
  emit();
  // Leftovers from before a reload: send them once runners have registered.
  setTimeout(retryAllPendingSaves, 1500);
}

async function attempt(key: string): Promise<void> {
  const entry = entries.get(key);
  if (!entry || entry.inFlight || entry.status === "failed") return;
  const run = entry.run ?? runners[entry.stored.kind];
  if (!run || !isOnline() || !mine(entry)) {
    entry.status = "waiting";
    emit();
    return;
  }
  const sent = entry.stored;
  entry.inFlight = true;
  entry.again = false;
  entry.status = "saving";
  emit();
  let saved = false;
  let result: unknown;
  try {
    result = await run(sent.payload);
    saved = true;
  } catch (e) {
    entry.lastError = errorText(e);
    entry.refusals = isRefusal(e) ? entry.refusals + 1 : 0;
    if (entry.refusals >= MAX_REFUSALS && !entry.again) {
      // Retrying cannot fix this; stop, take it off the device, and say so on screen.
      entry.status = "failed";
      if (entry.persist) removeStorage(key);
      console.error(`[Fit2Lift save] ${key} refused by the server; giving up`, e);
    } else {
      entry.status = "waiting";
      console.warn(`[Fit2Lift save] ${key} not saved yet; will retry`, e);
    }
  }
  entry.inFlight = false;
  if (saved) {
    entry.refusals = 0;
    entry.lastError = null;
    if (entry.stored === sent && !entry.again) {
      entries.delete(key);
      if (entry.persist) removeStorage(key);
      if (entry.fromReload) confirmedAfterReload.set(key, sent);
    }
    try {
      entry.onSaved?.(result, sent.payload);
    } catch (e) {
      // The save itself landed; a screen callback failing must not mark it unsaved.
      console.error(`[Fit2Lift save] ${key} saved, but the screen update failed`, e);
    }
  }
  if (entry.discarded && entries.get(key) === entry) {
    entries.delete(key);
    emit();
    return;
  }
  if (entries.get(key) === entry && entry.again) {
    void attempt(key);
    return;
  }
  emit();
}

/* ---------- public API ---------- */

export function registerPendingSaveRunner(kind: PendingSaveKind, run: Runner): void {
  runners[kind] = run;
}

export type QueueSaveOptions<P> = {
  kind: PendingSaveKind;
  /** One key per item; a newer value for the same key replaces the older unsent one. */
  key: string;
  orgId: string;
  employeeId: string;
  payload: P;
  /**
   * `false` keeps the value in memory only (files, which are too big for localStorage).
   * Such items still retry while the page is open but are lost on reload.
   */
  persist?: boolean;
  /**
   * With `persist: false`: the caller keeps its own copy on the device (ROM clips in IndexedDB),
   * so the item is not counted or shown as lost-on-reload.
   */
  durable?: boolean;
  /** Overrides the registered runner (needed when the payload is not plain JSON). */
  run?: (payload: P) => Promise<unknown>;
  /** Called after the server confirms this value (only while the page that queued it is open). */
  onSaved?: (result: unknown, payload: P) => void;
};

/** Save now, or keep and retry until the server confirms. */
export function queueSave<P>(opts: QueueSaveOptions<P>): void {
  ensureStarted();
  const persist = opts.persist !== false;
  const stored: StoredEntry = {
    v: 1,
    key: opts.key,
    kind: opts.kind,
    userId: currentUserId,
    orgId: opts.orgId,
    employeeId: opts.employeeId,
    payload: opts.payload,
    queuedAt: Date.now(),
  };
  if (persist) writeStorage(stored);
  confirmedAfterReload.delete(opts.key);
  const existing = entries.get(opts.key);
  if (existing) {
    existing.stored = stored;
    existing.persist = persist;
    existing.durable = opts.durable === true;
    existing.run = opts.run as Runner | undefined;
    existing.onSaved = opts.onSaved as LiveEntry["onSaved"];
    existing.discarded = false;
    existing.fromReload = false;
    existing.refusals = 0;
    if (existing.status === "failed") existing.status = "waiting";
    if (existing.inFlight) {
      existing.again = true;
      return;
    }
  } else {
    entries.set(opts.key, {
      stored,
      persist,
      durable: opts.durable === true,
      status: "waiting",
      inFlight: false,
      again: false,
      discarded: false,
      fromReload: false,
      refusals: 0,
      lastError: null,
      run: opts.run as Runner | undefined,
      onSaved: opts.onSaved as LiveEntry["onSaved"],
    });
  }
  void attempt(opts.key);
}

/**
 * The newest value this device holds for an item that the server copy may not reflect yet:
 * an unsent one (from memory, or from before a reload), or a reload leftover that landed while
 * the screen was loading. Only the signed-in author's items are returned; failed ones never are.
 */
export function readPendingSave<P>(key: string): P | null {
  ensureStarted();
  const live = entries.get(key);
  if (live) return live.status !== "failed" && mine(live) ? (live.stored.payload as P) : null;
  const confirmed = confirmedAfterReload.get(key);
  if (confirmed && mine(confirmed)) return confirmed.payload as P;
  const stored = readStorage(key);
  return stored && mine(stored) ? (stored.payload as P) : null;
}

/** Drop an item without sending it (e.g. the record it belonged to was replaced). */
export function discardPendingSave(key: string): void {
  const e = entries.get(key);
  if (e?.inFlight) e.discarded = true;
  else entries.delete(key);
  confirmedAfterReload.delete(key);
  removeStorage(key);
  emit();
}

/**
 * Resolves once no save for this item is in flight (immediately if none is). Use after
 * `discardPendingSave` when the record it targets is about to change, so a late write can't land
 * on the new record.
 */
export function whenPendingSaveIdle(key: string, timeoutMs = 10_000): Promise<void> {
  if (!entries.get(key)?.inFlight) return Promise.resolve();
  return new Promise((resolve) => {
    // A request can hang for minutes on bad wifi; don't hold staff up longer than this.
    const timer = setTimeout(resolve, timeoutMs);
    const list = idleWaiters.get(key) ?? [];
    list.push(() => {
      clearTimeout(timer);
      resolve();
    });
    idleWaiters.set(key, list);
  });
}

/** Clear the "couldn't be saved" items once staff have seen them. */
export function dismissFailedPendingSaves(): void {
  for (const [key, e] of entries) {
    if (e.status === "failed" && mine(e)) entries.delete(key);
  }
  emit();
}

export const pendingSaveKeys = {
  fmsManual: (employeeId: string) => `fms_manual:${employeeId}`,
  romDegrees: (employeeId: string) => `rom_degrees:${employeeId}`,
  romAnalysis: (mediaId: string) => `rom_analysis:${mediaId}`,
  romVideo: (employeeId: string, movementId: string) => `rom_video:${employeeId}:${movementId}`,
  postureSide: (postureImageId: string) => `posture_side:${postureImageId}`,
  postureFront: (postureImageId: string) => `posture_front:${postureImageId}`,
};
